import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Application, Batch } from "../api";

const StyledCard = styled.div<{ disabled?: boolean }>`
    display: block;
    padding: 15px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.2s ease;
    background-color: white;
    width: 100%;
    box-sizing: border-box;
    margin-bottom: 15px;
    opacity: ${props => props.disabled ? 0.6 : 1};

    &:hover {
        border-color: #9051a8;
        transform: translateY(-2px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    @media (max-width: 768px) {
        padding: 12px;
        transform: none;

        &:hover {
            transform: none;
            box-shadow: none;
            border-color: #9051a8;
        }
    }
`;

const CardTitle = styled.div`
    font-size: 18px;
    color: #333;
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 600;
    margin-bottom: 10px;

    @media (max-width: 768px) {
        font-size: 16px;
        gap: 8px;
    }
`;

const CardDescription = styled.div`
    font-size: 14px;
    line-height: 1.6;
    color: #555;
    font-weight: 400;
`;

const DescriptionItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
`;

const StatusBadge = styled.span<{ status: string }>`
    display: inline-block;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    background-color: ${props => {
        switch (props.status.toLowerCase()) {
            case 'complete':
            case 'accepted':
                return '#e6f4ea';
            case 'in progress':
            case 'pending':
                return '#fff3e0';
            case 'cancelled':
                return '#feebee';
            default:
                return '#f8f9ff';
        }
    }};
    color: ${props => {
        switch (props.status.toLowerCase()) {
            case 'complete':
            case 'accepted':
                return '#1e8e3e';
            case 'in progress':
            case 'pending':
                return '#fb8c00';
            case 'cancelled':
                return '#d32f2f';
            default:
                return '#666';
        }
    }};
`;

const StyledLink = styled(Link)`
    text-decoration: none;
    color: inherit;

    &:hover {
        text-decoration: none;
    }
`;

interface Props {
    application: Application;
    batches?: Batch[];
    disabled?: boolean;
}

export const ApplicationCard = (props: Props) => {
    const batch = props.batches?.find(b => b._id === props.application.batchId);
    const { status, createdAt } = props.application;
    
    const formattedDate = new Date(createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });

    const CardContent = (
        <StyledCard disabled={props.disabled}>
            <CardTitle>
                <FontAwesomeIcon icon="file-alt" size="1x" />
                {batch?.name || "Your application"}
            </CardTitle>
            <CardDescription>
                <DescriptionItem>
                    Applied on {formattedDate}
                </DescriptionItem>
                <DescriptionItem>
                    Status: <StatusBadge status={status}>{status}</StatusBadge>
                </DescriptionItem>
            </CardDescription>
        </StyledCard>
    );

    if (props.disabled) {
        return CardContent;
    }

    return (
        <StyledLink to={`/application/${props.application._id}`}>
            {CardContent}
        </StyledLink>
    );
};